import type { SessionStorageMode, SessionToken } from './types';

const STORAGE_KEY = 'gp_session_token';

/**
 * Which store a load/save/clear should use. Both fields come from the iframe
 * URL (`session_storage`, `embed_key`) and are fixed for the page's lifetime.
 * Required rather than defaulted on purpose: a silent fallback would hide the
 * one mistake that matters — clearing only the per-tab copy and leaving a dead
 * token behind in localStorage.
 */
export interface TokenStore {
  mode: SessionStorageMode;
  embedKey: string | null;
}

// In-memory fallback so a blocked Web Storage (Safari ITP, sandboxed iframe
// with storage denied) degrades to per-load memory instead of throwing on
// boot. Same try/catch pattern as currency/store.ts.
const memory = new Map<string, string>();

function write(getStorage: () => Storage, key: string, value: string): void {
  memory.set(key, value);
  try {
    getStorage().setItem(key, value);
  } catch {
    // storage unavailable — memory fallback already holds the value
  }
}

function read(getStorage: () => Storage, key: string): string | null {
  try {
    return getStorage().getItem(key);
  } catch {
    return memory.get(key) ?? null;
  }
}

function remove(getStorage: () => Storage, key: string): void {
  memory.delete(key);
  try {
    getStorage().removeItem(key);
  } catch {
    // storage unavailable — memory entry already removed
  }
}

// localStorage on this origin is shared by every operator's embed, so the
// persistent copy is namespaced by embed_key. sessionStorage keeps the plain
// key: it is per-tab and cannot bleed, and not renaming it means tabs open
// across a deploy keep their session.
function localKey(embedKey: string): string {
  return `${STORAGE_KEY}:${embedKey}`;
}

// Persistence is opt-in per embed (`session_storage=local`) AND needs an
// embed_key to namespace the entry. Without one we would be back to a single
// global localStorage key — a fresh tab with any embed_key loading the
// previous player's token, which is why the token was per-tab to begin with.
// So that combination degrades to sessionStorage instead.
function persistentKey(store: TokenStore): string | null {
  if (store.mode !== 'local' || store.embedKey === null) return null;
  return localKey(store.embedKey);
}

export function saveToken(token: SessionToken, store: TokenStore): void {
  const key = persistentKey(store);
  if (key === null) write(() => window.sessionStorage, STORAGE_KEY, token);
  else write(() => window.localStorage, key, token);
}

export function loadToken(store: TokenStore): SessionToken | null {
  const key = persistentKey(store);
  // Default mode never reads localStorage. The read is deliberately NOT
  // symmetric: a token that some other embed opted to persist on this shared
  // origin must never be adopted by an embed that did not opt in.
  if (key === null) return read(() => window.sessionStorage, STORAGE_KEY) as SessionToken | null;
  // Opted in: persistent copy first, then the per-tab one, so a session that
  // started before the host added the flag survives the switch.
  const persisted = read(() => window.localStorage, key);
  return (persisted ?? read(() => window.sessionStorage, STORAGE_KEY)) as SessionToken | null;
}

/**
 * Where a token actually lives for this store, and whether one is there.
 *
 * For the Dev Console readout: a native host that passes
 * `session_storage=local` has no debugger to check the flag took effect with,
 * and the one way it silently does not is a missing `embed_key` — which drops
 * the embed back to per-tab storage. This reports the store that is really in
 * use rather than the one that was asked for.
 */
export function describeTokenStore(store: TokenStore): {
  effective: SessionStorageMode;
  key: string;
  hasToken: boolean;
} {
  const key = persistentKey(store);
  if (key === null) {
    return {
      effective: 'session',
      key: STORAGE_KEY,
      hasToken: read(() => window.sessionStorage, STORAGE_KEY) !== null,
    };
  }
  return {
    effective: 'local',
    key,
    hasToken: read(() => window.localStorage, key) !== null,
  };
}

export function clearToken(store: TokenStore): void {
  // Both stores, in both modes. A dead token left in localStorage would be
  // resurrected by the next opt-in load and pin the player to the Expired
  // screen on every launch — strictly worse than no persistence at all.
  remove(() => window.sessionStorage, STORAGE_KEY);
  if (store.embedKey !== null) remove(() => window.localStorage, localKey(store.embedKey));
}
